<script setup lang="ts">
import { computed, ref } from 'vue';

export type JsonImportEntity = { title: string; summary?: string; content?: string; text?: string; fields?: Record<string, unknown> };
export type JsonImportSegment = JsonImportEntity & { scene?: string };
export type JsonImportScene = JsonImportEntity & { segments?: JsonImportSegment[] };
export type JsonImportChapter = JsonImportEntity & { scenes?: JsonImportScene[] };
export type JsonImportPayload = {
  chapters?: JsonImportChapter[];
  scenes?: JsonImportScene[];
  segments?: JsonImportSegment[];
  characters?: JsonImportEntity[];
  locations?: JsonImportEntity[];
};

const props = defineProps<{ busy: boolean; error: string; result: string }>();
const emit = defineEmits<{ close: []; import: [payload: JsonImportPayload] }>();

const source = ref(`{
  "chapters": [
    {
      "title": "Kapitel 1 – Ankunft",
      "summary": "Die Gruppe erreicht Chronis.",
      "content": "Hier steht der lange Fließtext, der im großen Schreibbereich erscheint.",
      "scenes": [
        {
          "title": "Szene 1 – Das Tor",
          "segments": [
            { "title": "Ankunft", "content": "Die Tore öffnen sich." }
          ]
        }
      ]
    }
  ],
  "characters": [{ "title": "Mara", "summary": "Kartografin" }],
  "locations": [{ "title": "Chronis", "summary": "Hauptstadt" }]
}`);
const parseError = ref('');
const modelGuide = computed(() => `Erzeuge ausschließlich gültiges JSON – kein Markdown, keine Kommentare, kein Text davor oder danach.

Erlaubte Wurzel-Schlüssel: chapters, scenes, segments, characters, locations.
- chapters: Kapitel mit optionalen scenes; eine Szene kann optionale segments enthalten.
- scenes: eigenständige Szenen mit optionalen segments.
- segments: eigenständige Segmente; hierfür ist "scene" Pflicht und muss exakt dem Titel einer vorhandenen oder im selben JSON erzeugten Szene entsprechen.
- characters und locations: Basis-Entities.

Jeder Eintrag benötigt "title". Optional: "summary", "content" (alternativ "text") und "fields".
- "summary" ist ausschließlich eine kurze Kartenbeschreibung, idealerweise ein Satz.
- "content" bzw. "text" ist der vollständige lange Fließtext für den großen Schreibbereich der Entity. Nutze dieses Feld für Biografien, Szenen- oder Kapiteltext; es wird nicht in die Kurzbeschreibung geschrieben.
- "fields" darf nur bekannte Chronis-Feldschlüssel dieses Templates verwenden; Referenzen darin nur als bestehende UUIDs.

Beispiel:
{
  "chapters": [{
    "title": "Kapitel 1 – Ankunft",
    "summary": "Die Gruppe landet auf Chronis.",
    "content": "Der lange Kapiteltext steht hier im großen Schreibbereich.",
    "scenes": [{
      "title": "Szene 1 – Das Tor",
      "content": "Der lange Szenentext steht im großen Schreibbereich der Szene.",
      "segments": [
        { "title": "Ankunft", "content": "Die Tore öffnen sich." },
        { "title": "Entscheidung", "content": "Mara tritt vor." }
      ]
    }]
  }],
  "scenes": [{ "title": "Zwischenszene", "segments": [{ "title": "Beobachtung" }] }],
  "segments": [{ "scene": "Zwischenszene", "title": "Nachtrag", "content": "Eine Nachricht trifft ein." }],
  "characters": [{ "title": "Mara", "summary": "Kartografin", "text": "Mara kartografiert die Randgebiete von Chronis und …" }],
  "locations": [{ "title": "Chronis", "summary": "Hauptstadt", "content": "Die Hauptstadt liegt zwischen …" }]
}`);

function entityIsValid(value: unknown, path: string, errors: string[], needsScene = false): value is JsonImportEntity {
  if (!value || typeof value !== 'object' || Array.isArray(value)) { errors.push(`${path} muss ein Objekt sein.`); return false; }
  const item = value as Record<string, unknown>;
  if (typeof item.title !== 'string' || !item.title.trim()) errors.push(`${path}.title fehlt.`);
  for (const key of ['summary', 'content', 'text']) if (item[key] !== undefined && typeof item[key] !== 'string') errors.push(`${path}.${key} muss Text sein.`);
  if (item.fields !== undefined && (!item.fields || typeof item.fields !== 'object' || Array.isArray(item.fields))) errors.push(`${path}.fields muss ein Objekt sein.`);
  if (needsScene && (typeof item.scene !== 'string' || !item.scene.trim())) errors.push(`${path}.scene fehlt.`);
  return true;
}
function list(value: unknown, path: string, errors: string[]) {
  if (value === undefined) return [];
  if (!Array.isArray(value)) { errors.push(`${path} muss eine Liste sein.`); return []; }
  return value;
}
function validate(payload: Record<string, unknown>) {
  const errors: string[] = [];
  const allowed = new Set(['chapters', 'scenes', 'segments', 'characters', 'locations']);
  for (const key of Object.keys(payload)) if (!allowed.has(key)) errors.push(`Unbekannter Wurzel-Schlüssel: ${key}.`);
  for (const [kind, value] of [['chapters', payload.chapters], ['scenes', payload.scenes], ['characters', payload.characters], ['locations', payload.locations]] as const) {
    list(value, kind, errors).forEach((item, index) => {
      if (!entityIsValid(item, `${kind}[${index}]`, errors)) return;
      const nestedScenes = kind === 'chapters' ? list((item as Record<string, unknown>).scenes, `${kind}[${index}].scenes`, errors) : [];
      nestedScenes.forEach((scene, sceneIndex) => {
        if (!entityIsValid(scene, `${kind}[${index}].scenes[${sceneIndex}]`, errors)) return;
        list((scene as Record<string, unknown>).segments, `${kind}[${index}].scenes[${sceneIndex}].segments`, errors)
          .forEach((segment, segmentIndex) => entityIsValid(segment, `${kind}[${index}].scenes[${sceneIndex}].segments[${segmentIndex}]`, errors));
      });
      if (kind === 'scenes') list((item as Record<string, unknown>).segments, `${kind}[${index}].segments`, errors)
        .forEach((segment, segmentIndex) => entityIsValid(segment, `${kind}[${index}].segments[${segmentIndex}]`, errors));
    });
  }
  list(payload.segments, 'segments', errors).forEach((item, index) => entityIsValid(item, `segments[${index}]`, errors, true));
  return errors;
}
function submit() {
  parseError.value = '';
  let parsed: unknown;
  try { parsed = JSON.parse(source.value); }
  catch (error) { parseError.value = `Ungültiges JSON: ${error instanceof Error ? error.message : 'Syntaxfehler'}`; return; }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) { parseError.value = 'Das JSON muss ein Objekt mit Listen wie „chapters“ oder „characters“ sein.'; return; }
  const errors = validate(parsed as Record<string, unknown>);
  if (errors.length) { parseError.value = errors.slice(0, 5).join(' '); return; }
  emit('import', parsed as JsonImportPayload);
}
async function copyGuide() {
  try { await navigator.clipboard.writeText(modelGuide.value); }
  catch { /* Das Handbuch bleibt sichtbar und kann manuell kopiert werden. */ }
}
</script>

<template>
  <div class="modal-backdrop json-import-backdrop" @click.self="!busy && emit('close')">
    <section class="create-dialog json-import-dialog" aria-label="JSON-Schnellimport">
      <header><div><span class="eyebrow">KI-Schnellimport</span><h2>Inhalte aus JSON anlegen</h2><p>Mehrere Kapitel, Szenen, Segmente, Charaktere und Orte in einem Durchgang.</p></div><button type="button" class="json-import-close" :disabled="busy" @click="emit('close')">×</button></header>
      <details class="json-import-guide" open><summary>Modell-Anleitung &amp; Schema</summary><textarea readonly :value="modelGuide" aria-label="Anleitung für das Modell" /><button type="button" @click="copyGuide">Anleitung kopieren</button></details>
      <label class="json-import-source">JSON einfügen<textarea v-model="source" :disabled="busy" spellcheck="false" /></label>
      <p v-if="parseError || error" class="dialog-error">{{ parseError || error }}</p>
      <p v-if="result" class="json-import-result">{{ result }}</p>
      <footer class="dialog-actions"><button type="button" class="ghost" :disabled="busy" @click="emit('close')">Schließen</button><button type="button" :disabled="busy" @click="submit">{{ busy ? 'Wird angelegt …' : 'JSON importieren' }}</button></footer>
    </section>
  </div>
</template>

<style scoped>
.json-import-dialog{width:min(920px,calc(100vw - 32px));max-height:calc(100vh - 36px);overflow:auto}.json-import-dialog>header{display:flex;align-items:flex-start;justify-content:space-between;gap:16px}.json-import-dialog h2{margin:4px 0;color:var(--ui-text)}.json-import-dialog p{color:var(--ui-text-muted)}.json-import-close{border:0;background:transparent;color:var(--ui-text-soft);font-size:24px}.json-import-guide{margin:18px 0;border:1px solid var(--ui-border);border-radius:9px;background:var(--ui-surface-2);padding:10px}.json-import-guide summary{cursor:pointer;color:var(--ui-action-text);font-weight:800}.json-import-guide textarea,.json-import-source textarea{box-sizing:border-box;width:100%;resize:vertical;border:1px solid var(--ui-border-strong);border-radius:7px;color:var(--ui-text);background:var(--ui-surface-1);font:12px/1.5 ui-monospace,Consolas,monospace}.json-import-guide textarea{min-height:270px;margin-top:10px;padding:10px}.json-import-guide button{margin-top:8px}.json-import-source{display:block;color:var(--ui-text-soft);font-weight:800}.json-import-source textarea{min-height:300px;margin-top:7px;padding:12px}.json-import-result{border:1px solid var(--ui-success-border);border-radius:7px;color:var(--ui-success-text)!important;background:var(--ui-success-surface);padding:9px 10px}.dialog-actions{position:sticky;bottom:0;padding-top:12px;background:var(--ui-surface-2)}
</style>
